// import { provideAbpCore, withOptions } from '@abp/ng.core';
import { DialogModule } from '@angular/cdk/dialog';
import { provideHttpClient } from '@angular/common/http';
import {
    ApplicationConfig,
    importProvidersFrom,
    LOCALE_ID,
    provideBrowserGlobalErrorListeners,
    provideZoneChangeDetection,
    signal,
} from '@angular/core';
import { BrowserModule, provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';

import { Environment, provideAbpCore, withOptions } from '@abp/ng.core';
import {
    DefaultTitleStrategy,
    provideRouter,
    TitleStrategy,
    withInMemoryScrolling,
    withRouterConfig,
} from '@angular/router';
import { provideAuiConfirmation } from '@ral/ui:aui/confirmation';
import { provideAuiToastService } from '@ral/ui:aui/toast';
import { provideValidationMessages } from '@ral/utils:core/validation';
import { provideTanStackQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';
import { provideAuiIconConfig } from '@ral/utils:core/ui';
import { provideAuiTranslations } from '@ral/utils:core/ui';

// Register Czech locale data

// // See https://github.com/rollup/plugins/tree/master/packages/dynamic-import-vars#limitations for supported dynamic import formats. If this is intended to be left as-is, you can use the /* @vite-ignore */ comment inside the import() call to suppress this warning.
export function registerLocale() {
    return import(`@angular/common/locales/cs`);
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideClientHydration(withEventReplay()),
        provideBrowserGlobalErrorListeners(),
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(
            appRoutes,
            withRouterConfig({
                paramsInheritanceStrategy: 'always',
                onSameUrlNavigation: 'reload',
            }),
            withInMemoryScrolling({
                scrollPositionRestoration: 'disabled',
                anchorScrolling: 'disabled',
            })
        ),
        importProvidersFrom([BrowserModule, BrowserAnimationsModule]),

        provideTanStackQuery(
            new QueryClient({
                defaultOptions: {
                    queries: {
                        staleTime: 200,
                        refetchOnMount: true,
                    },
                },
            })
            // withDevtools(() => ({ loadDevtools: 'auto' }))
        ),
        // provideStore({ router: routerReducer }),
        importProvidersFrom([DialogModule]),

        provideAnimations(),
        provideHttpClient(),

        provideAuiToastService,
        provideAuiConfirmation,

        provideAbpCore(
            withOptions({
                environment: environment as Environment,
                registerLocaleFn: registerLocale,
                skipGetAppConfiguration: true,
                skipInitAuthService: true,
            })
        ),
        { provide: TitleStrategy, useClass: DefaultTitleStrategy },
        {
            provide: LOCALE_ID,
            useValue: 'cs',
        },
        provideAuiIconConfig(() => ({ useAssets: signal(true) })),
        provideValidationMessages(() => {
            return {
                required: () => 'Toto pole je povinné',
                requiredTrue: () => 'Pro odeslání je nutné souhlasit',
                email: () => 'Zadejte platnou emailovou adresu',
                minlength: (args) => `Minimální délka je ${args.requiredLength}`,
                maxlength: (args) => `Maximální délka je ${args.requiredLength}`,
                pattern: () => `Zadejte platný formát - pouze čísla, +, -, (, ) a mezera`, // ${args.requiredPattern}`,
                nullValidator: () => 'Toto pole je povinné',
                invalidBirthNumber: () => 'Zadejte platné rodné číslo',
                dateIsInFuture: () => 'Datum nemůže být v budoucnosti',
                dateIsInThePast: () => 'Datum nemůže být v minulosti',
                invalidPhoneNumber: () => 'Zadejte platné telefonní číslo',
            };
        }),
        provideAuiTranslations(() => {
            return {
                noItems: () => 'Žádná hodnota',
                navigateBack: () => 'Zpět',
                error404Title: () => 'Entita nebyla nalezena',
                error404Message: () => 'Entita mohla být přesunuta nebo odstraněna.',
                error403Title: () => 'Neoprávněný přístup',
                error403Message: () => 'Pro zobrazení této stránky nemáte dostatečná oprávnění.',
                error500Title: () => 'Chyba na serveru',
                error500Message: () => 'Na serveru nastala neočekávaná chyba.',
                entityValidation: () => 'Neplatná entita',
                errorGenericTitle: () => 'Error',
                errorGenericMessage: () => 'Nastala neočekávaná chyba',
                phoneInputPlaceholder: () => '+420 123 456 789',
                phoneInputCodePlaceholder: () => 'Předvolba',
                phoneInputFilterCodePlaceholder: () => 'Vyhledat předvolbu',
            };
        }),

        // {
        //     provide: 'Window',
        //     useFactory: () => {
        //         if (typeof window !== 'undefined') {
        //             return window;
        //         } else {
        //             return {
        //                 scrollTo: { y: 0 },
        //             };
        //         }
        //     },
        // },
    ],
};
